/**
 * Redimensiona una imagen si su ancho excede el máximo especificado.
 * Mantiene la proporción original (height auto).
 * 
 * @param file - Archivo de imagen a redimensionar
 * @param maxWidth - Ancho máximo en píxeles (default: 800)
 * @param quality - Calidad de compresión (0-1, default: 0.9)
 * @returns Promise<File> - Archivo redimensionado o el original si no necesita redimensionarse
 */
export async function resizeImageIfNeeded(
  file: File,
  maxWidth: number = 800,
  quality: number = 0.9
): Promise<File> {
  // Solo procesar imágenes
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Formatos soportados para redimensionamiento
  const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!supportedFormats.includes(file.type.toLowerCase())) {
    console.log('[ImageResize] Formato no soportado para redimensionamiento:', file.type);
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();

    // Timeout de seguridad para evitar que la promesa quede colgada
    const timeoutId = window.setTimeout(() => {
      console.warn('[ImageResize] Timeout al procesar imagen, devolviendo archivo original');
      resolve(file);
    }, 30000); // 30 segundos máximo

    let objectUrl: string | null = null;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // ignore
        }
        objectUrl = null;
      }
    };

    const process = () => {
      cleanup();

      // Validar dimensiones antes de procesar
      if (!img.width || !img.height || img.width <= 0 || img.height <= 0) {
        console.warn('[ImageResize] Dimensiones inválidas, devolviendo archivo original');
        resolve(file);
        return;
      }

      // Si la imagen es menor o igual al máximo, devolver original
      if (img.width <= maxWidth) {
        console.log(`[ImageResize] Imagen no necesita redimensionamiento (${img.width}px <= ${maxWidth}px)`);
        resolve(file);
        return;
      }

      // Calcular nuevo tamaño manteniendo proporción
      const ratio = maxWidth / img.width;
      const newWidth = maxWidth;
      const newHeight = Math.round(img.height * ratio);

      console.log(`[ImageResize] Redimensionando de ${img.width}x${img.height} a ${newWidth}x${newHeight}`);

      // Crear canvas y redimensionar
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.warn('[ImageResize] No se pudo obtener contexto del canvas, devolviendo archivo original');
        resolve(file);
        return;
      }

      // Configurar calidad de renderizado
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Dibujar imagen redimensionada
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // Convertir a Blob y luego a File
      // Usar el tipo original del archivo, pero convertir PNG a JPEG si es necesario para mejor compresión
      const outputType = file.type.toLowerCase() === 'image/png' ? 'image/jpeg' : file.type;

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.warn('[ImageResize] No se pudo crear blob, devolviendo archivo original');
            resolve(file);
            return;
          }

          // Mantener el nombre original pero cambiar extensión si cambió el tipo
          const originalName = file.name;
          const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
          const newExt = outputType === 'image/jpeg' ? 'jpg' : (outputType.split('/')[1] || 'jpg');
          const newFileName = `${nameWithoutExt}.${newExt}`;

          const resizedFile = new File([blob], newFileName, {
            type: outputType,
            lastModified: Date.now(),
          });

          console.log(`[ImageResize] Imagen redimensionada: ${file.size} bytes -> ${resizedFile.size} bytes`);
          resolve(resizedFile);
        },
        outputType,
        quality
      );
    };

    img.onload = process;
    img.onerror = (error) => {
      cleanup();
      console.warn('[ImageResize] Error al cargar imagen, devolviendo archivo original:', error);
      resolve(file);
    };

    // Usar ObjectURL es mucho más rápido que FileReader + base64 para imágenes grandes.
    try {
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (e) {
      console.warn('[ImageResize] No se pudo crear ObjectURL, fallback a FileReader:', e);
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (typeof ev.target?.result === 'string') {
          try {
            img.src = ev.target.result;
          } catch (srcError) {
            cleanup();
            console.warn('[ImageResize] Error al establecer src de imagen, devolviendo archivo original:', srcError);
            resolve(file);
          }
        } else {
          cleanup();
          console.warn('[ImageResize] No se pudo obtener resultado del FileReader, devolviendo archivo original');
          resolve(file);
        }
      };
      reader.onerror = () => {
        cleanup();
        console.warn('[ImageResize] Error al leer archivo, devolviendo archivo original');
        resolve(file);
      };
      reader.readAsDataURL(file);
    }
  });
}

/** No comprimir flyers ya livianos (evita trabajo innecesario en cliente). */
const FLYER_UPLOAD_MIN_BYTES_TO_COMPRESS = 200 * 1024;
/** Borde máximo tras resize (suficiente para detalle + transforms CDN). */
const FLYER_UPLOAD_MAX_EDGE_PX = 1200;

/**
 * Para uploads de flyer: si el archivo es grande, reduce a WebP (buen balance peso/calidad).
 * Archivos &lt; ~200KB se suben tal cual.
 */
export async function compressImageForFlyerUpload(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) {
    return file;
  }

  const supportedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
  if (!supportedFormats.includes(file.type.toLowerCase())) {
    return file;
  }

  if (file.size < FLYER_UPLOAD_MIN_BYTES_TO_COMPRESS) {
    return file;
  }

  return new Promise((resolve) => {
    const img = new Image();
    const timeoutId = window.setTimeout(() => {
      console.warn("[ImageResize] Timeout en compressImageForFlyerUpload, usando original");
      resolve(file);
    }, 30000);

    let objectUrl: string | null = null;

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // ignore
        }
        objectUrl = null;
      }
    };

    const finishWebp = (canvas: HTMLCanvasElement) => {
      canvas.toBlob(
        (blob) => {
          cleanup();
          if (!blob) {
            console.warn("[ImageResize] WebP blob vacío, usando original");
            resolve(file);
            return;
          }
          const base =
            file.name.substring(0, file.name.lastIndexOf(".")) || file.name || "flyer";
          resolve(new File([blob], `${base}.webp`, { type: "image/webp", lastModified: Date.now() }));
        },
        "image/webp",
        0.85,
      );
    };

    const process = () => {
      if (!img.width || !img.height || img.width <= 0 || img.height <= 0) {
        cleanup();
        console.warn("[ImageResize] Dimensiones inválidas en compressImageForFlyerUpload");
        resolve(file);
        return;
      }

      const maxDim = Math.max(img.width, img.height);
      const scale = maxDim > FLYER_UPLOAD_MAX_EDGE_PX ? FLYER_UPLOAD_MAX_EDGE_PX / maxDim : 1;
      const newWidth = Math.round(img.width * scale);
      const newHeight = Math.round(img.height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        cleanup();
        resolve(file);
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      finishWebp(canvas);
    };

    img.onload = process;
    img.onerror = () => {
      cleanup();
      console.warn("[ImageResize] Error cargando imagen en compressImageForFlyerUpload");
      resolve(file);
    };

    try {
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch {
      cleanup();
      resolve(file);
    }
  });
}

